import { useState } from "react";
import { FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePrivy } from "@privy-io/react-auth";
import { LoadingButton } from "@/components/LoadingButton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Wallet } from "lucide-react";
import { Container } from "@/components/Container";
import { useAuth } from "@/hooks/use-auth.tsx";

export function UserProfile() {
  const {
    linkEmail,
    linkGoogle,
    linkWallet,
    user,
    unlinkEmail,
    unlinkGoogle,
    unlinkWallet,
  } = usePrivy();
  const [loading, setLoading] = useState<
    "email" | "google" | "wallet" | string | null
  >(null);

  const linkedAccounts = user?.linkedAccounts || [];
  const lastAccount = linkedAccounts.length === 1;

  return (
    <Container className="pt-0 px-4 sm:px-6 md:px-8">
      <div className="space-y-6">
        {/* User Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>
              View and manage your account details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UserInfo />
          </CardContent>
        </Card>

        {/* Linked Accounts and Wallets Card */}
        <Card>
          <CardHeader>
            <CardTitle>Linked Accounts & Wallets</CardTitle>
            <CardDescription>
              Manage your connected accounts and authentication methods
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email Account */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 border rounded-md gap-3">
              <div className="flex items-center gap-2">
                <div>
                  <p className="font-medium">Email</p>
                  {user?.email ? (
                    <p className="text-sm text-muted-foreground break-all">
                      {user.email.address}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No email linked
                    </p>
                  )}
                </div>
              </div>
              {!user?.email ? (
                <LoadingButton
                  className="w-full sm:w-36 px-4 py-2"
                  disabled={loading != null}
                  loading={loading === "email"}
                  onClick={() => {
                    setLoading("email");
                    Promise.resolve(linkEmail()).finally(() =>
                      setLoading(null),
                    );
                  }}
                >
                  Link Email
                </LoadingButton>
              ) : (
                <Tooltip>
                  <TooltipTrigger className="w-full sm:w-auto">
                    <LoadingButton
                      className="w-full sm:w-36 px-4 py-2"
                      variant="outline"
                      disabled={lastAccount || loading != null}
                      loading={loading === "email"}
                      onClick={() => {
                        if (user.email?.address) {
                          setLoading("email");
                          Promise.resolve(
                            unlinkEmail(user.email.address!),
                          ).finally(() => setLoading(null));
                        }
                      }}
                    >
                      Unlink Email
                    </LoadingButton>
                  </TooltipTrigger>
                  {lastAccount && (
                    <TooltipContent className="max-w-md">
                      <p className="text-sm">
                        You must have at least one linked account
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              )}
            </div>

            {/* Google Account */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 border rounded-md gap-3">
              <div className="flex items-center gap-2">
                <div>
                  <p className="font-medium">Google</p>
                  {user?.google ? (
                    <p className="text-sm text-muted-foreground break-all">
                      {user.google.email}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No Google account linked
                    </p>
                  )}
                </div>
              </div>
              {!user?.google ? (
                <LoadingButton
                  className="w-full sm:w-36 px-4 py-2"
                  disabled={loading != null}
                  loading={loading === "google"}
                  onClick={() => {
                    setLoading("google");
                    Promise.resolve(linkGoogle()).finally(() =>
                      setLoading(null),
                    );
                  }}
                >
                  Link Google
                </LoadingButton>
              ) : (
                <Tooltip>
                  <TooltipTrigger className="w-full sm:w-auto">
                    <LoadingButton
                      className="w-full sm:w-36 px-4 py-2"
                      variant="outline"
                      disabled={lastAccount || loading != null}
                      loading={loading === "google"}
                      onClick={() => {
                        if (user.google?.subject) {
                          setLoading("google");
                          Promise.resolve(
                            unlinkGoogle(user.google.subject!),
                          ).finally(() => setLoading(null));
                        }
                      }}
                    >
                      Unlink Google
                    </LoadingButton>
                  </TooltipTrigger>
                  {lastAccount && (
                    <TooltipContent className="max-w-md">
                      <p className="text-sm">
                        You must have at least one linked account
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              )}
            </div>

            {/* Display linked wallets */}
            {user?.linkedAccounts
              .filter((account) => account.type === "wallet")
              .map((wallet, index) => (
                <div
                  key={wallet.address}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 border rounded-md gap-3"
                >
                  <div className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-primary flex-shrink-0" />
                    <div>
                      <p className="font-medium">Linked Wallet {index + 1}</p>
                      <p className="text-sm text-muted-foreground break-all">
                        {wallet.address}
                      </p>
                    </div>
                  </div>
                  <Tooltip>
                    <TooltipTrigger className="w-full sm:w-auto">
                      <LoadingButton
                        className="w-full sm:w-36 px-4 py-2"
                        variant="outline"
                        disabled={lastAccount || loading != null}
                        loading={loading === `wallet-${index}`}
                        onClick={() => {
                          if (wallet.address) {
                            setLoading(`wallet-${index}`);
                            Promise.resolve(
                              unlinkWallet(wallet.address),
                            ).finally(() => setLoading(null));
                          }
                        }}
                      >
                        Unlink Wallet
                      </LoadingButton>
                    </TooltipTrigger>
                    {lastAccount && (
                      <TooltipContent className="max-w-md">
                        <p className="text-sm">
                          You must have at least one linked account
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
              ))}

            {(!user?.linkedAccounts.some(
              (account) => account.type === "wallet",
            ) && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 border rounded-md gap-3">
                <div className="flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="font-medium">Wallet</p>
                    <p className="text-sm text-muted-foreground">
                      No wallet linked
                    </p>
                  </div>
                </div>
                <LoadingButton
                  className="w-full sm:w-36 px-4 py-2"
                  disabled={loading != null}
                  loading={loading === "wallet"}
                  onClick={() => {
                    setLoading("wallet");
                    Promise.resolve(linkWallet()).finally(() =>
                      setLoading(null),
                    );
                  }}
                >
                  Link Wallet
                </LoadingButton>
              </div>
            )) || (
              <div className="flex justify-center mt-4">
                <LoadingButton
                  className="w-full sm:w-48 px-4 py-2"
                  disabled={loading != null}
                  loading={loading === "wallet"}
                  onClick={() => {
                    setLoading("wallet");
                    Promise.resolve(linkWallet()).finally(() =>
                      setLoading(null),
                    );
                  }}
                >
                  Link Another Wallet
                </LoadingButton>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Linked wallets are used for authentication. You can link multiple
              blockchain wallets including Ethereum, Solana, and others.
            </p>
          </CardContent>
        </Card>
      </div>
    </Container>
  );
}

function UserInfo() {
  const { username } = useAuth();

  return (
    <FormItem>
      <Label htmlFor="username">Username</Label>
      <Input id="username" readOnly value={username} disabled />
    </FormItem>
  );
}
